import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { CountTagItem, TagText } from "components/atoms/Tag";
import Textarea from "components/atoms/Textarea";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button/Button";
import StyledText from "components/atoms/text/StyledText";

import BodyWithBreadcrumb from "components/molecules/BodyWithBreadcrumb";
import Breadcrumb from "components/molecules/Breadcrumb";
import ReferenceDetailTitleInput from "components/organisms/reference/ReferenceDetailTitleInput";
import PDFChatPageLayout from "components/organisms/review/PDFChatPageLayout";
import PDFChatSidebar from "components/organisms/review/PDFChatSidebar";
import AbstractModal from "components/page/modal/AbstractModal";
import ReferenceBibInfoEditModal from "components/page/modal/ReferenceBibInfoEditModal";
import ReferenceCitationEditModal from "components/page/modal/ReferenceCitationEditModal";
import ReferenceCitationStyleModal from "components/page/modal/ReferenceCitationStyleModal";
import ReferenceFolderEditModal from "components/page/modal/ReferenceFolderEditModal";
import ReferenceTagEditModal from "components/page/modal/ReferenceTagEditModal";
import LoginUserLayout from "components/templates/LoginUserLayout";

import {
    useBibliographies,
    useBibliography,
    useBibliographyDelete,
    useBibliographyUpdate,
    useFolders,
} from "hooks/queries/useBibliographies";
import { useUser } from "hooks/queries/useUser";
import { usePDFChatWebSocket } from "hooks/usePDFChatWebSocket";
import { useSessionPageHistory } from "hooks/useSessionPageHistory";

import Constants from "utils/constants";
import { getDateFromString, getTimeFromString } from "utils/dateUtil";
import { genericEnterKeyDown } from "utils/validation";

const HeaderLeftSec = styled.div`
    display: flex;
    align-items: center;
    column-gap: 8px;
    width: 100%;
    max-width: calc(100% - 60px);
`;

const HeaderRightSec = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 52px;
    margin: 0 0 0 8px;
`;

const MainContainer = styled.div`
    max-width: ${Constants.TABLET_WIDTH}px;
    margin-bottom: 96px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    height: 20px;
    gap: 4px;
    margin-bottom: 8px;
`;

const SectionHeaderTitle = styled(StyledText)`
    font-size: 1.4rem;
    font-weight: 500;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const BibInfoSection = styled.div`
    margin-bottom: 40px;
`;

const BibInfoText = styled(StyledText).attrs({
    fontSize: "regular",
    color: "var(--color-Grey1)",
})``;

const AbstractSection = styled.div`
    flex: 1;
    margin-top: 16px;
    margin-bottom: 40px;
    min-height: 32px;
`;

const AbstractTextarea = styled(Textarea)``;

const AnnotaionSection = styled.div`
    display: flex;
    margin-bottom: 40px;
`;

const AnnotationLeftSection = styled.div`
    width: 100%;
    max-width: ${Constants.TABLET_WIDTH / 2}px;
`;

const ReferenceLinkItem = styled.div`
    width: 100%;
    margin-bottom: 16px;
`;

const ReferenceLinkItemHeader = styled(SectionHeader)``;

const ReferenceLinkItemHeaderTitle = styled(StyledText)`
    font-size: 1.4rem;
    font-weight: 500;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const ReferenceLinkTagItemWrap = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: auto;
    min-height: 20px;
`;

const ReferenceLinkTagItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: ${(props) => props.margin || "0"};
    padding: 0px 8px;
    height: 20px;
    outline: none;
    background-color: var(--color-Tag);
    border: transparent;
    border-radius: 4px;
`;

const ReferenceLinkItemBlankBox = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    height: 20px;
`;

const ReferenceLinkItemBlankText = styled(StyledText)`
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const AnnotationRightSection = styled.div`
    width: 100%;
    max-width: ${Constants.TABLET_WIDTH / 2}px;

    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MemoSection = styled.div`
    flex: 1;

    display: flex;
    flex-direction: column;
`;

const MemoTitleText = styled(StyledText).attrs((props) => ({
    fontSize: "regular",
    fontWeight: "bold",
}))`
    margin: 0 0 8px 0;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const MemoDiv = styled.div`
    flex: 1;
`;

const MemoTextarea = styled.textarea`
    padding: 4px 6px;
    width: 100%;
    min-height: 100%;
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    background-color: var(--color-White);
    border: solid 1px #ecf2f4;
    border-radius: 2px;
    resize: none;
    outline: none;

    &::placeholder {
        color: var(--color-Grey2);
    }
`;

const DateSection = styled.div`
    flex: 0 0 auto;

    display: flex;
    justify-content: end;
    align-items: center;
    gap: 8px;
`;

const DateText = styled(StyledText).attrs((props) => ({
    fontSize: "small",
    color: "var(--color-Grey1)",
}))``;

const ReferenceListSection = styled.div`
    width: 100%;
`;

const ReferenceListHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 20px;
    margin-bottom: 8px;
`;

const ReferenceListTitleText = styled(SectionHeaderTitle)``;

const ReferenceListParamsWrap = styled.div`
    display: flex;
    align-items: end;
    flex-wrap: wrap;
    margin-bottom: 8px;
`;

const ReferenceFilterTagItem = styled(ReferenceLinkTagItem)`
    transition: all 120ms ease-in;
    background-color: var(--color-Tag);
    cursor: pointer;

    &.active {
        background-color: var(--color-TagActive);
    }
`;

const ReferenceListContent = styled.div``;

const ReferenceListBlankContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 80px;
`;

const ReferenceListBlankText = styled(StyledText)``;

const ReferenceListItemRow = styled.div`
    padding: 8px 0px;

    border-bottom: solid 1px var(--color-Background);
    cursor: default;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        transition: all 80ms ease-in;
        cursor: pointer;
        background-color: var(--color-ListHover);
    }
`;

const ReferenceListItemTitle = styled(StyledText)``;

const ReferenceListItemText = styled(StyledText).attrs({
    fontSize: "small",
    color: "var(--color-Grey1)",
})``;

function ReferencePage(props) {
    const { reference_uuid } = useParams();
    const navigate = useNavigate();

    // 사용자 처리
    const [user, setUser] = useState({});
    const userQuery = useUser();

    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    // 레퍼런스 처리
    const [reference, setReference] = useState({});
    const referenceQuery = useBibliography({ uuid: reference_uuid });
    useEffect(() => {
        if (referenceQuery.data) {
            setReference(referenceQuery.data);
        }
    }, [referenceQuery.data]);

    useEffect(() => {
        return () => {
            referenceQuery.remove();
        };
    }, []);

    // region: 목록 - 상세 네비게이션
    const [searchParams, setSearchParams] = useState();
    const { prevPage } = useSessionPageHistory();

    const loadSessionCache = () => {
        const sessionCacheJson = sessionStorage.getItem("referenceList");
        if (sessionCacheJson) {
            const sessionCacheObj = JSON.parse(sessionCacheJson);
            setSearchParams(sessionCacheObj.searchParams);
        }
    };

    useEffect(() => {
        if (prevPage && prevPage.startsWith("/reference")) {
            loadSessionCache();
        }
    }, [prevPage]);

    // region: 웹소켓 훅
    const {
        threads,
        selectedThread,
        setSelectedThread,
        profiles,
        streamingMessageIds,
        autoScrollToEnd,
        setAutoScrollToEnd,
        pdfIndexingInfo,
        chatStart,
    } = usePDFChatWebSocket({
        reference_uuid,
        user,
        connectionDependencies: [reference_uuid, userQuery.data],
        onFileUploaded: () => {
            referenceQuery.refetch();
        },
        onFileDeleted: () => {
            referenceQuery.refetch();
        },
        onPdfMetadata: () => {
            referenceQuery.refetch();
        },
    });

    // 서지정보
    const abstractTextareaRef = useRef(null);

    const updateBibliographyMutation = useBibliographyUpdate();
    const deleteBibliographyMutation = useBibliographyDelete();
    const handleReferenceFieldChanged = (event) => {
        const { name, value } = event.target;
        // bib_json 내부 필드는 "bib_json.필드명" 형태로 name을 넣어줄 수 있음
        if (name.startsWith("bib_json.")) {
            setReference((prev) => ({
                ...prev,
                bib_json: {
                    ...prev.bib_json,
                    [name.replace("bib_json.", "")]: value,
                },
            }));
        } else {
            setReference((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };
    const updateReferenceField = (fields) => {
        if (!reference.id) return;
        const updateData = { id: reference.id };

        Object.entries(fields).forEach(([key, value]) => {
            // bib_json.* 필드는 bib_json 객체 내부로 병합
            if (key.startsWith("bib_json.")) {
                if (!updateData.bib_json) updateData.bib_json = { ...reference.bib_json };
                updateData.bib_json[key.replace("bib_json.", "")] = value;
            } else {
                updateData[key] = value;
            }
        });

        updateBibliographyMutation.mutate(updateData);
    };

    // 참고 문헌 삭제
    const handleDeleteButtonClick = () => {
        let message = "참고문헌을 삭제하시겠습니까?";
        if (reference.citation_count > 0) {
            message = "리서치에 인용된 참고문헌입니다. 삭제하시겠습니까?";
        }
        Confirm("warn", "참고문헌 삭제", message, "삭제", (e) => {
            deleteBibliographyMutation.mutate({
                ids: [reference.id],
            });
            navigate(-1);
        });
    };

    // 폴더
    const folderQuery = useFolders();
    const [referenceFolder, setReferenceFolder] = useState(null);
    useEffect(() => {
        if (folderQuery.data || reference.folder) {
            // 폴더 목록에서 현재 참고문헌이 포함된 폴더를 찾음
            setReferenceFolder(folderQuery.data.find((folder) => folder.id === reference.folder));
        }
    }, [folderQuery.data, reference.folder]);

    // 메모
    const memoTextareaRef = useRef();

    useEffect(() => {
        const textarea = memoTextareaRef.current;
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
    }, [reference?.memo]);

    // 모달
    const [showCitationStyleModal, setShowCitationStyleModal] = useState(false);
    const [showFolderEditModal, setShowFolderEditModal] = useState(false);
    const [showTagEditModal, setShowTagEditModal] = useState(false);
    const [showCitationEditModal, setShowCitationEditModal] = useState(false);
    const [showBibInfoEditModal, setShowBibInfoEditModal] = useState(false);

    // region: 연결된 참고문헌
    const defaultReferenceListParams = {
        page: 1,
        page_size: 100,
        ordering: "-created_at",
    };
    const [referenceList, setReferenceList] = useState([]);
    const [referenceListParams, setReferenceListParams] = useState(null);
    const referenceListQuery = useBibliographies(referenceListParams);

    useEffect(() => {
        if (referenceQuery.data && referenceListParams === null) {
            const newParams = {
                ...defaultReferenceListParams,
                folder: referenceQuery.data.folder,
                tags: referenceQuery.data.tag_mappings.map((tag) => tag.id).join(","),
                researches: referenceQuery.data.researches.map((research) => research.id).join(","),
            };
            setReferenceListParams(newParams);
        }
    }, [referenceQuery.data]);

    useEffect(() => {
        if (referenceListParams) {
            referenceListQuery.refetch();
        }
    }, [referenceListParams]);

    useEffect(() => {
        if (referenceListQuery.data) {
            setReferenceList(referenceListQuery.data.results);
        }
    }, [referenceListQuery.data]);

    const referenceListParamsRenderer = () => {
        const paramELs = [];
        // 1. 폴더 표시
        if (referenceList && referenceListParams && referenceFolder) {
            const is_active = referenceFolder.id === referenceListParams.folder;
            paramELs.push(
                <Tooltip key="folder" message={is_active ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}>
                    <ReferenceFilterTagItem
                        className={is_active ? "active" : ""}
                        margin={"0 8px 2px 0"}
                        onClick={() => {
                            if (is_active) {
                                setReferenceListParams((prev) => ({
                                    ...prev,
                                    folder: null,
                                }));
                            } else {
                                if (referenceListParams.folder === null) {
                                    setReferenceListParams((prev) => ({
                                        ...prev,
                                        folder: referenceFolder.id,
                                    }));
                                }
                            }
                        }}
                    >
                        <Icon name="folder" size="8" color="var(--color-SubBlack)" />
                        <TagText style={{ margin: "0 8px 0 8px" }} ellipsis>
                            {referenceFolder.name} (
                            {referenceList.filter((r) => r.folder === referenceFolder.id).length})
                        </TagText>
                        <Icon name={is_active ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                    </ReferenceFilterTagItem>
                </Tooltip>,
            );
        }
        // 2. 태그 표시
        if (referenceList && referenceListParams && reference.tag_mappings) {
            const tags = referenceListParams.tags
                .split(",")
                .filter((tag_id) => !!tag_id)
                .map((tag_id) => reference.tag_mappings.find((item) => item.id === Number(tag_id)))
                .filter(Boolean); // undefined 필터

            paramELs.push(
                ...reference.tag_mappings.map((tag, index) => {
                    const tag_ids = tags.map((t) => t.id);
                    const is_active = tag_ids.includes(tag.id);

                    return (
                        <Tooltip
                            key={index + "TAG_FILTER"}
                            message={is_active ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}
                        >
                            <ReferenceFilterTagItem
                                className={is_active ? "active" : ""}
                                margin={"0 8px 2px 0"}
                                onClick={(e) => {
                                    if (is_active) {
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            tags: prev.tags
                                                .split(",")
                                                .filter((id) => Number(id) !== tag.id)
                                                .join(","),
                                        }));
                                    } else {
                                        tag_ids.push(tag.id);
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            tags: tag_ids.join(","),
                                        }));
                                    }
                                }}
                            >
                                <Icon name="tag" size="8" color="var(--color-SubBlack)" />
                                <TagText style={{ margin: "0 8px 0 8px" }} ellipsis>
                                    {tag.name} (
                                    {
                                        referenceList.filter((r) => r.tag_mappings.map((t) => t.id).includes(tag.id))
                                            .length
                                    }
                                    )
                                </TagText>
                                <Icon name={is_active ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                            </ReferenceFilterTagItem>
                        </Tooltip>
                    );
                }),
            );
        }
        // 3. 리서치 표시
        if (referenceList && referenceListParams && reference.researches) {
            const researches = referenceListParams.researches
                .split(",")
                .filter((research_id) => !!research_id)
                .map((research_id) => reference.researches.find((item) => item.id === Number(research_id)))
                .filter(Boolean); // undefined 필터

            paramELs.push(
                ...reference.researches.map((research, index) => {
                    const research_ids = researches.map((r) => r.id);
                    const is_active = research_ids.includes(research.id);

                    return (
                        <Tooltip
                            key={index + "RESEARCH_FILTER"}
                            message={is_active ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}
                        >
                            <ReferenceFilterTagItem
                                className={is_active ? "active" : ""}
                                margin={"0 8px 2px 0"}
                                onClick={(e) => {
                                    if (is_active) {
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            researches: prev.researches
                                                .split(",")
                                                .filter((id) => Number(id) !== research.id)
                                                .join(","),
                                        }));
                                    } else {
                                        research_ids.push(String(research.id));
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            researches: research_ids.join(","),
                                        }));
                                    }
                                }}
                            >
                                <Icon name="tag" size="8" color="var(--color-SubBlack)" />
                                <TagText style={{ margin: "0 8px 0 8px" }} ellipsis>
                                    {research.display_name} (
                                    {
                                        referenceList.filter((r) => r.researches.map((t) => t.id).includes(research.id))
                                            .length
                                    }
                                    )
                                </TagText>
                                <Icon name={is_active ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                            </ReferenceFilterTagItem>
                        </Tooltip>
                    );
                }),
            );
        }
        return paramELs;
    };

    // region: 마크업
    return (
        <LoginUserLayout searchParams={searchParams}>
            <HtmlHead title={reference?.display_name} />
            <PDFChatPageLayout
                main={
                    <>
                        <Breadcrumb style={{ maxWidth: "864px" }}>
                            <HeaderLeftSec>
                                <Tooltip message={"뒤로"}>
                                    <Button
                                        onlyIcon
                                        bgColor={"var(--color-Button4)"}
                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                        fontColor={"var(--color-Black)"}
                                        onClick={() => {
                                            navigate(-1);
                                        }}
                                        disabled={window.history.length === 1}
                                    >
                                        <Icon name="arrowLeft" size="12px" />
                                    </Button>
                                </Tooltip>
                                {/* 참고문헌 표시 이름 */}
                                <ReferenceDetailTitleInput
                                    name={"display_name"}
                                    placeholder={"참고문헌 이름을 입력해주세요."}
                                    value={reference?.display_name}
                                    allowEmpty={false}
                                    onChange={handleReferenceFieldChanged}
                                    saveAct={(e) => updateReferenceField({ display_name: reference?.display_name })}
                                />
                            </HeaderLeftSec>
                            <HeaderRightSec>
                                {/* 북마크 버튼 */}
                                <Tooltip message={"즐겨찾기"}>
                                    <Button
                                        onlyIcon
                                        border={
                                            reference?.is_favorite
                                                ? "solid 2px var(--color-Key)"
                                                : "solid 2px var(--color-DisabledButton)"
                                        }
                                        bgColor={"var(--color-White)"}
                                        hoverBgColor={"none"}
                                        onClick={(e) => updateReferenceField({ is_favorite: !reference.is_favorite })}
                                    >
                                        <Icon
                                            name={"bookmark"}
                                            size={"12"}
                                            color={
                                                reference?.is_favorite
                                                    ? "var(--color-Key)"
                                                    : "var(--color-DisabledButton)"
                                            }
                                        />
                                    </Button>
                                </Tooltip>

                                {/* 참고문헌 삭제 버튼 */}
                                <Tooltip message={"참고문헌 삭제"}>
                                    <Button
                                        onlyIcon
                                        bgColor={"var(--color-Button2)"}
                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                        onClick={handleDeleteButtonClick}
                                    >
                                        <Icon name={"delete"} size={"12"} color={"var(--color-White)"} />
                                    </Button>
                                </Tooltip>
                            </HeaderRightSec>
                        </Breadcrumb>

                        <BodyWithBreadcrumb>
                            <MainContainer>
                                {/* 서지정보 */}
                                <BibInfoSection>
                                    <SectionHeader>
                                        <SectionHeaderTitle>서지정보</SectionHeaderTitle>
                                        <Tooltip message={"서지정보 수정"}>
                                            <Button
                                                onlyIcon
                                                buttonSize={"20px"}
                                                bgColor={"transparent"}
                                                hoverBgColor={"var(--color-ButtonHover4)"}
                                                onClick={() => setShowBibInfoEditModal(true)}
                                            >
                                                <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip message={"인용 스타일"}>
                                            <Button
                                                onlyIcon
                                                buttonSize={"20px"}
                                                bgColor={"transparent"}
                                                hoverBgColor={"var(--color-ButtonHover4)"}
                                                onClick={() => setShowCitationStyleModal(true)}
                                            >
                                                <Icon name={"list"} color={"var(--color-SubBlack)"} />
                                            </Button>
                                        </Tooltip>
                                    </SectionHeader>
                                    <BibInfoText>{reference?.styled_text || ""}</BibInfoText>
                                </BibInfoSection>

                                {/* 초록 */}
                                <AbstractSection>
                                    <SectionHeader>
                                        <SectionHeaderTitle>초록</SectionHeaderTitle>
                                    </SectionHeader>
                                    {reference?.file &&
                                    reference?.file_status === "DONE" &&
                                    !reference?.bib_json?.abstract ? (
                                        <Tooltip message={"PDF 파일에서 추출 중입니다."}>
                                            <AiCursor />
                                        </Tooltip>
                                    ) : (
                                        <AbstractTextarea
                                            ref={abstractTextareaRef}
                                            placeholder={"PDF 파일을 업로드하여 자동으로 추출할 수 있습니다."}
                                            name={"bib_json.abstract"}
                                            value={reference?.bib_json?.abstract}
                                            onChange={handleReferenceFieldChanged}
                                            saveAct={(e) =>
                                                updateReferenceField({
                                                    "bib_json.abstract": reference?.bib_json?.abstract,
                                                })
                                            }
                                            padding={"2px"}
                                        />
                                    )}
                                </AbstractSection>

                                <AnnotaionSection>
                                    <AnnotationLeftSection>
                                        {/* 폴더 */}
                                        <ReferenceLinkItem>
                                            <ReferenceLinkItemHeader>
                                                <ReferenceLinkItemHeaderTitle>폴더</ReferenceLinkItemHeaderTitle>
                                                <Tooltip message={"폴더 연결"}>
                                                    <Button
                                                        onlyIcon
                                                        buttonSize={"20px"}
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={() => setShowFolderEditModal(true)}
                                                    >
                                                        <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                                    </Button>
                                                </Tooltip>
                                            </ReferenceLinkItemHeader>
                                            {referenceFolder && referenceList ? (
                                                <ReferenceLinkTagItemWrap>
                                                    <ReferenceLinkTagItem margin={"0 8px 2px 0"}>
                                                        <TagText ellipsis>{referenceFolder.name}</TagText>
                                                    </ReferenceLinkTagItem>
                                                </ReferenceLinkTagItemWrap>
                                            ) : (
                                                <ReferenceLinkItemBlankBox>
                                                    <ReferenceLinkItemBlankText>
                                                        연결된 폴더가 없습니다.
                                                    </ReferenceLinkItemBlankText>
                                                </ReferenceLinkItemBlankBox>
                                            )}
                                        </ReferenceLinkItem>

                                        {/* 태그 */}
                                        <ReferenceLinkItem>
                                            <ReferenceLinkItemHeader>
                                                <ReferenceLinkItemHeaderTitle>태그</ReferenceLinkItemHeaderTitle>
                                                <Tooltip message={"태그 연결"}>
                                                    <Button
                                                        onlyIcon
                                                        buttonSize={"20px"}
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={() => setShowTagEditModal(true)}
                                                    >
                                                        <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                                    </Button>
                                                </Tooltip>
                                            </ReferenceLinkItemHeader>
                                            {reference?.tag_mappings?.length > 0 && referenceList ? (
                                                <ReferenceLinkTagItemWrap>
                                                    {reference.tag_mappings.map((tag, index) => (
                                                        <ReferenceLinkTagItem
                                                            key={index + "TAG"}
                                                            margin={"0 8px 2px 0"}
                                                        >
                                                            <TagText ellipsis>{tag.name}</TagText>
                                                        </ReferenceLinkTagItem>
                                                    ))}
                                                </ReferenceLinkTagItemWrap>
                                            ) : (
                                                <ReferenceLinkItemBlankBox>
                                                    <ReferenceLinkItemBlankText>
                                                        연결된 태그가 없습니다.
                                                    </ReferenceLinkItemBlankText>
                                                </ReferenceLinkItemBlankBox>
                                            )}
                                        </ReferenceLinkItem>

                                        {/* 리서치 */}
                                        <ReferenceLinkItem>
                                            <ReferenceLinkItemHeader>
                                                <ReferenceLinkItemHeaderTitle>리서치</ReferenceLinkItemHeaderTitle>
                                                <Tooltip message={"리서치 연결"}>
                                                    <Button
                                                        onlyIcon
                                                        buttonSize={"20px"}
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={() => setShowCitationEditModal(true)}
                                                    >
                                                        <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                                    </Button>
                                                </Tooltip>
                                            </ReferenceLinkItemHeader>
                                            {reference?.researches?.length > 0 && referenceList ? (
                                                <ReferenceLinkTagItemWrap>
                                                    {reference.researches.map((research, index) => (
                                                        <ReferenceLinkTagItem
                                                            key={index + "RESEARCH"}
                                                            margin={"0 8px 2px 0"}
                                                        >
                                                            <TagText ellipsis>{research.display_name}</TagText>
                                                        </ReferenceLinkTagItem>
                                                    ))}
                                                </ReferenceLinkTagItemWrap>
                                            ) : (
                                                <ReferenceLinkItemBlankBox>
                                                    <ReferenceLinkItemBlankText>
                                                        연결된 리서치가 없습니다.
                                                    </ReferenceLinkItemBlankText>
                                                </ReferenceLinkItemBlankBox>
                                            )}
                                        </ReferenceLinkItem>
                                    </AnnotationLeftSection>

                                    <AnnotationRightSection>
                                        {/* 메모 */}
                                        <MemoSection>
                                            <MemoTitleText>메모</MemoTitleText>
                                            <MemoDiv>
                                                <MemoTextarea
                                                    ref={memoTextareaRef}
                                                    rows={1}
                                                    name={"memo"}
                                                    placeholder={"입력된 메모가 없습니다."}
                                                    value={reference?.memo || ""}
                                                    onChange={handleReferenceFieldChanged}
                                                    onKeyDown={(e) => {
                                                        genericEnterKeyDown(e, () => {
                                                            if (e.shiftKey) {
                                                                handleReferenceFieldChanged(e);
                                                            } else {
                                                                //blur 처리
                                                                e.preventDefault();
                                                                memoTextareaRef.current.blur();
                                                            }
                                                        });
                                                    }}
                                                    onBlur={(e) => {
                                                        updateReferenceField({ memo: reference.memo });
                                                    }}
                                                />
                                            </MemoDiv>
                                        </MemoSection>
                                        {/* 추가 / 최근 활동 날짜 */}
                                        <DateSection>
                                            <DateText>
                                                추가 {getDateFromString(reference.created_at)}{" "}
                                                {getTimeFromString(reference.created_at)}
                                            </DateText>
                                            <DateText>|</DateText>
                                            <DateText>
                                                최근 활동 {getDateFromString(reference.updated_at)}{" "}
                                                {getTimeFromString(reference.updated_at)}
                                            </DateText>
                                        </DateSection>
                                    </AnnotationRightSection>
                                </AnnotaionSection>

                                <ReferenceListSection>
                                    <ReferenceListHeader>
                                        <ReferenceListTitleText>연결된 참고문헌</ReferenceListTitleText>
                                        <CountTagItem>
                                            <TagText>
                                                {referenceListParams?.folder === null &&
                                                referenceListParams?.tags === "" &&
                                                referenceListParams?.researches === ""
                                                    ? 0
                                                    : referenceList?.length}{" "}
                                                건
                                            </TagText>
                                        </CountTagItem>
                                    </ReferenceListHeader>
                                    <ReferenceListParamsWrap>{referenceListParamsRenderer()}</ReferenceListParamsWrap>
                                    <ReferenceListContent>
                                        {referenceList &&
                                            (referenceList.length === 0 ||
                                            (referenceListParams?.folder === null &&
                                                referenceListParams?.tags === "" &&
                                                referenceListParams?.researches === "") ? (
                                                <ReferenceListBlankContainer>
                                                    <Icon name={"folder"} size={"32"} color={"var(--color-Grey1)"} />
                                                    <ReferenceListBlankText>
                                                        연결된 참고문헌이 없습니다.
                                                    </ReferenceListBlankText>
                                                </ReferenceListBlankContainer>
                                            ) : (
                                                referenceList.map((item, idx) => (
                                                    <ReferenceListItemRow
                                                        key={idx}
                                                        onClick={(e) => {
                                                            window.open(`/reference/${item.uuid}`);
                                                        }}
                                                    >
                                                        <ReferenceListItemTitle>
                                                            {item.display_name}
                                                        </ReferenceListItemTitle>
                                                        <ReferenceListItemText>
                                                            {item.styled_text}
                                                        </ReferenceListItemText>
                                                    </ReferenceListItemRow>
                                                ))
                                            ))}
                                    </ReferenceListContent>
                                </ReferenceListSection>
                            </MainContainer>
                        </BodyWithBreadcrumb>
                    </>
                }
                sidebar={
                    <PDFChatSidebar
                        user={user}
                        reference={reference}
                        threads={threads}
                        selectedThread={selectedThread}
                        setSelectedThread={setSelectedThread}
                        profiles={profiles}
                        chatStart={chatStart}
                        streamingMessageIds={streamingMessageIds}
                        autoScrollToEnd={autoScrollToEnd}
                        setAutoScrollToEnd={setAutoScrollToEnd}
                        pdfIndexingInfo={pdfIndexingInfo}
                    />
                }
            />
            {showCitationStyleModal && (
                <AbstractModal
                    width={480}
                    height={240}
                    modalTitle="인용 스타일"
                    exitModal={(e) => setShowCitationStyleModal(false)}
                >
                    <ReferenceCitationStyleModal
                        exitModal={(e) => setShowCitationStyleModal(false)}
                        reference={reference}
                    />
                </AbstractModal>
            )}
            {showCitationEditModal && (
                <AbstractModal
                    width={480}
                    modalTitle="리서치 연결"
                    exitModal={(e) => setShowCitationEditModal(!showCitationEditModal)}
                >
                    <ReferenceCitationEditModal
                        exitModal={(e) => setShowCitationEditModal(!showCitationEditModal)}
                        citedResearches={reference.researches}
                        referenceId={reference?.id}
                    />
                </AbstractModal>
            )}
            {showFolderEditModal && (
                <AbstractModal width={480} modalTitle="폴더 연결" exitModal={() => setShowFolderEditModal(false)}>
                    <ReferenceFolderEditModal
                        referenceIds={[reference.id]}
                        selectedFolderId={reference.folder}
                        exitModal={() => setShowFolderEditModal(false)}
                    />
                </AbstractModal>
            )}
            {showTagEditModal && (
                <AbstractModal
                    width={480}
                    modalTitle="태그 연결"
                    exitModal={(e) => {
                        setShowTagEditModal(false);
                    }}
                >
                    <ReferenceTagEditModal
                        referenceId={reference.id}
                        referenceFile={reference.file}
                        selectedTagIds={reference.tag_mappings.map((tag) => tag.id)}
                        exitModal={(e) => {
                            setShowTagEditModal(false);
                        }}
                    />
                </AbstractModal>
            )}
            {showBibInfoEditModal && (
                <AbstractModal
                    width={560}
                    modalTitle={"서지정보"}
                    exitModal={(e) => {
                        setShowBibInfoEditModal(false);
                    }}
                >
                    <ReferenceBibInfoEditModal
                        reference={reference}
                        exitModal={(e) => {
                            setShowBibInfoEditModal(false);
                        }}
                    />
                </AbstractModal>
            )}
        </LoginUserLayout>
    );
}

export default ReferencePage;
